import { AuthServiceClient } from "../auth_service_http_client";
import { MerchantAuthRepository } from "../repositories/MerchantAuthRepository";
import {
  MerchantChangePasswordVerifyModel,
  MerchantForgotPasswordVerifyOtpModel,
  MerchantLoginModel,
} from "@/models/MerchantAuthModel";
import {
  mapMerchantChangePasswordVerifyModel,
  mapMerchantForgotPasswordVerifyOtpModel,
  mapMerchantLoginModel,
} from "@/adapters/merchant_auth_adapter";
import { ErrorModel } from "@/models/ErrorModel";

function toErrorModel(error: any, fallbackMessage: string): ErrorModel {
  return new ErrorModel({
    code: error?.response?.data?.code ?? error?.response?.status ?? -1,
    message:
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      error?.message ??
      fallbackMessage,
  });
}

export class MerchantAuthRepositoryImp implements MerchantAuthRepository {
  private client: AuthServiceClient;

  constructor(client: AuthServiceClient) {
    this.client = client;
  }

  async login({
    phone,
    password,
  }: {
    phone: string;
    password: string;
  }): Promise<MerchantLoginModel> {
    try {
      const resp = await this.client.merchantAuthLogin.login2({
        merchantLoginRequest: { phone, password },
      });
      return mapMerchantLoginModel(resp.data);
    } catch (error: any) {
      throw toErrorModel(error, "Login failed");
    }
  }

  async verifyLoginOtp({
    phone,
    password,
    otp,
  }: {
    phone: string;
    password: string;
    otp: string;
  }): Promise<MerchantLoginModel> {
    try {
      const resp = await this.client.merchantAuthLogin.verifyLoginOtp({
        merchantVerifyLoginOtpRequest: { phone, password, otp },
      });
      return mapMerchantLoginModel(resp.data);
    } catch (error: any) {
      throw toErrorModel(error, "Verify login OTP failed");
    }
  }

  async resendLoginOtp({
    phone,
  }: {
    phone: string;
  }): Promise<MerchantLoginModel> {
    try {
      const resp = await this.client.merchantAuthLogin.resendLoginOtp1({
        merchantResendLoginOtpRequest: { phone },
      });
      return mapMerchantLoginModel(resp.data);
    } catch (error: any) {
      throw toErrorModel(error, "Resend OTP failed");
    }
  }

  async meChangePasswordVerify({
    currentPassword,
  }: {
    currentPassword: string;
  }): Promise<MerchantChangePasswordVerifyModel> {
    try {
      const resp = await this.client.merchantAuthLogin.meChangePasswordVerify({
        merchantChangePasswordVerifyRequest: { currentPassword },
      });
      return mapMerchantChangePasswordVerifyModel(resp.data);
    } catch (error: any) {
      throw toErrorModel(error, "Change password verify failed");
    }
  }

  async meChangePasswordSubmit({
    sessionId,
    token,
    newPassword,
    confirmNewPassword,
  }: {
    sessionId: string;
    token: string;
    newPassword: string;
    confirmNewPassword: string;
  }): Promise<boolean> {
    try {
      const resp = await this.client.merchantAuthLogin.meChangePasswordSubmit({
        merchantChangePasswordSubmitRequest: {
          sessionId,
          token,
          newPassword,
          confirmNewPassword,
        },
      });
      return Boolean(resp.data?.success ?? true);
    } catch (error: any) {
      throw toErrorModel(error, "Change password submit failed");
    }
  }

  async refresh({
    refreshToken,
  }: {
    refreshToken: string;
  }): Promise<MerchantLoginModel> {
    try {
      const resp = await this.client.merchantAuthLogin.refresh2({
        merchantRefreshTokenRequest: { refreshToken },
      });
      return mapMerchantLoginModel(resp.data);
    } catch (error: any) {
      throw toErrorModel(error, "Refresh failed");
    }
  }

  async logout({ refreshToken }: { refreshToken: string }): Promise<boolean> {
    try {
      const resp = await this.client.merchantAuthLogin.logout2({
        merchantLogoutRequest: { refreshToken },
      });
      return Boolean(resp.data?.success ?? true);
    } catch (error: any) {
      throw toErrorModel(error, "Logout failed");
    }
  }

  async forgotPasswordRequestOtp({
    phone,
  }: {
    phone: string;
  }): Promise<boolean> {
    try {
      const resp = await this.client.merchantAuthLogin.forgotPasswordRequestOtp(
        { merchantForgotPasswordRequestOtpRequest: { phone } },
      );
      return Boolean(resp.data?.success ?? true);
    } catch (error: any) {
      throw toErrorModel(error, "Request OTP failed");
    }
  }

  async forgotPasswordVerifyOtp({
    phone,
    otp,
  }: {
    phone: string;
    otp: string;
  }): Promise<MerchantForgotPasswordVerifyOtpModel> {
    try {
      const resp = await this.client.merchantAuthLogin.forgotPasswordVerifyOtp({
        merchantForgotPasswordVerifyOtpRequest: { phone, otp },
      });
      return mapMerchantForgotPasswordVerifyOtpModel(resp.data);
    } catch (error: any) {
      throw toErrorModel(error, "Verify OTP failed");
    }
  }

  async forgotPasswordSubmit({
    resetSessionToken,
    newPassword,
    confirmNewPassword,
  }: {
    resetSessionToken: string;
    newPassword: string;
    confirmNewPassword: string;
  }): Promise<boolean> {
    try {
      const resp = await this.client.merchantAuthLogin.forgotPasswordSubmit({
        merchantForgotPasswordSubmitRequest: {
          resetSessionToken,
          newPassword,
          confirmNewPassword,
        },
      });
      return Boolean(resp.data?.success ?? true);
    } catch (error: any) {
      throw toErrorModel(error, "Submit new password failed");
    }
  }

  async firstLoginChangePassword({
    sessionToken,
    newPassword,
    confirmNewPassword,
  }: {
    sessionToken: string;
    newPassword: string;
    confirmNewPassword: string;
  }): Promise<boolean> {
    try {
      await this.client.merchantAuthFirstLogin.changePassword2({
        merchantFirstLoginChangePasswordRequest: {
          sessionToken,
          newPassword,
          confirmNewPassword,
        },
      });
      return true;
    } catch (error: any) {
      throw toErrorModel(error, "First-login change password failed");
    }
  }
}
